import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { X, Search, UserPlus, Check, AlertCircle } from 'lucide-react';

const CollaboratorsModal = ({ isOpen, onClose, showId, showName, showDescription, currentCollaborators = [] }) => {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // First, get the current authenticated user
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setError('User not authenticated. Please log in again.');
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  // Define fetchFriends to use the currentUser from state
  const fetchFriends = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!currentUser) {
        console.error('No authenticated user found');
        setError('User not authenticated. Please log in again.');
        setLoading(false);
        return;
      }
      
      console.log('Fetching friends for user:', currentUser.uid);
      
      // Check if user has friends collection
      const friendsRef = collection(db, 'users', currentUser.uid, 'friends');
      const friendsSnapshot = await getDocs(friendsRef);
      
      console.log('Friends snapshot size:', friendsSnapshot.size);
      
      const friendsData = [];
      friendsSnapshot.forEach(friendDoc => {
        const friendData = friendDoc.data();
        console.log('Friend data:', friendData);
        
        // Check if this friend is already a collaborator
        const isCollaborator = currentCollaborators.some(
          collab => collab.id === friendData.friendUserId
        );
        
        if (!isCollaborator) {
          friendsData.push({
            id: friendData.friendUserId || friendDoc.id,
            name: friendData.friendName || 'Unknown',
            handle: friendData.friendHandle || 'unknown',
            photoURL: friendData.photoURL || null
          });
        }
      });
      
      console.log('Processed friends data:', friendsData);
      setFriends(friendsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching friends:', err);
      setError('Failed to load friends: ' + err.message);
      setLoading(false);
      setFriends([]); // Set empty array to prevent infinite loading
    }
  };
  
  // Use the fetchFriends function in useEffect when currentUser changes
  useEffect(() => {
    if (isOpen && currentUser) {
      fetchFriends();
    }
  }, [isOpen, currentUser, currentCollaborators]);
  
  if (!isOpen) return null;
  
  // Filter friends based on search query
  const filteredFriends = friends.filter(friend => 
    friend.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    friend.handle.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Toggle friend selection
  const toggleFriendSelection = (friendId) => {
    if (selectedFriends.includes(friendId)) {
      setSelectedFriends(selectedFriends.filter(id => id !== friendId));
    } else {
      setSelectedFriends([...selectedFriends, friendId]);
    }
  };
  
  // Update the sendCollaborationRequests function to use currentUser
  const sendCollaborationRequests = async () => {
    if (selectedFriends.length === 0 || !currentUser) return;
    
    try {
      setSending(true);
      setError(null);
      setSuccess(null);
      
      // Get Firebase functions instance
      const functions = getFunctions();
      const sendShowCollaborationRequest = httpsCallable(functions, 'sendShowCollaborationRequest');
      
      // Send requests to each selected friend
      for (const friendId of selectedFriends) {
        const friend = friends.find(f => f.id === friendId);
        
        await sendShowCollaborationRequest({
          showId,
          showName,
          showDescription,
          receiverId: friend.id,
          receiverHandle: friend.handle,
          receiverName: friend.name
        });
      }
      
      setSuccess(`Collaboration ${selectedFriends.length > 1 ? 'requests' : 'request'} sent successfully!`);
      setSelectedFriends([]);
    } catch (err) {
      console.error('Error sending collaboration requests:', err);
      setError(err.message || 'Failed to send collaboration requests');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      zIndex: 1200,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'rgb(255,255,255,.35)',
        borderRadius: '0.75rem',
        width: '90%',
        maxWidth: '30rem',
        backdropFilter: 'blur(5px)',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '1.5rem'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'white'
          }}>
            Show Collaborators
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Current collaborators section */}
        <div style={{
          marginBottom: '1.5rem'
        }}>
          <h3 style={{
            fontSize: '1.1rem',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '0.75rem'
          }}>
            Current Collaborators
          </h3>
          
          {currentCollaborators.length > 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {currentCollaborators.map(collaborator => (
                <div 
                  key={collaborator.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.5rem'
                  }}
                >
          
                  <div>
                    <div style={{
                      fontWeight: 'bold',
                      color: 'white'
                    }}>
                      {collaborator.name}
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#ccc'
                    }}>
                      @{collaborator.handle}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              padding: '1rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '0.5rem',
              color: '#ccc',
              textAlign: 'center'
            }}>
              You're the only collaborator on this show
            </div>
          )}
        </div>
        
        {/* Invite friends section */}
        <div>
          <h3 style={{
            fontSize: '1.1rem',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '0.75rem'
          }}>
            Invite Friends to Collaborate
          </h3>
          
          {/* Search bar */}
          <div style={{
            position: 'relative',
            marginBottom: '1rem'
          }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search friends..."
              style={{
                width: 'calc(100% - 4rem)',
                padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '0.9rem'
              }}
            />
            <Search 
              size={18} 
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999'
              }}
            />
          </div>
          
          {/* Friends list */}
          {loading ? (
            <div style={{
              padding: '1rem',
              textAlign: 'center',
              color: '#ccc',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '0.5rem'
            }}>
              <div style={{ marginBottom: '0.5rem' }}>Loading friends...</div>
    
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          ) : error ? (
            <div style={{
              padding: '1rem',
              backgroundColor: 'rgba(229, 9, 20, 0.2)',
              borderRadius: '0.5rem',
              color: '#E50914',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          ) : filteredFriends.length > 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              maxHeight: '15rem',
              overflowY: 'auto',
              padding: '0.25rem'
            }}>
              {filteredFriends.map(friend => (
                <div 
                  key={friend.id}
                  onClick={() => toggleFriendSelection(friend.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    backgroundColor: selectedFriends.includes(friend.id) 
                      ? 'rgba(255, 255, 255, 0.2)' 
                      : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
              
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 'bold',
                      color: 'white'
                    }}>
                      {friend.name}
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#ccc'
                    }}>
                      @{friend.handle}
                    </div>
                  </div>
                  
                  <div style={{
                    width: '1.5rem',
                    height: '1.5rem',
                    borderRadius: '50%',
                    backgroundColor: selectedFriends.includes(friend.id) ? 'white' : 'transparent',
                    border: '2px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {selectedFriends.includes(friend.id) && (
                      <Check size={14} color="#6600cc" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              padding: '1rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '0.5rem',
              color: '#ccc',
              textAlign: 'center'
            }}>
              {searchQuery 
                ? 'No friends match your search' 
                : 'No friends available to invite'}
            </div>
          )}
          
          {/* Error and success messages */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem',
              backgroundColor: 'rgba(229, 9, 20, 0.2)',
              borderRadius: '0.5rem',
              color: '#E50914',
              marginTop: '1rem'
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}
          
          {success && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem',
              backgroundColor: 'rgba(46, 204, 113, 0.2)',
              borderRadius: '0.5rem',
              color: '#2ecc71',
              marginTop: '1rem'
            }}>
              <Check size={16} />
              {success}
            </div>
          )}
          
          {/* Send invites button */}
          <button
            onClick={sendCollaborationRequests}
            disabled={selectedFriends.length === 0 || sending}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: selectedFriends.length === 0 ? 'rgba(255, 255, 255, 0.3)' : 'white',
              color: selectedFriends.length === 0 ? '#999' : '#6600cc',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: selectedFriends.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: '1.5rem'
            }}
          >
            <UserPlus size={18} />
            {sending 
              ? 'Sending...' 
              : `Send ${selectedFriends.length > 0 ? selectedFriends.length : ''} Invitation${selectedFriends.length !== 1 ? 's' : ''}`}
          </button>
          
          {/* Retry button */}
          {error && (
            <button
              onClick={() => {
                setLoading(true);
                setError(null);
                fetchFriends(); // Now this is properly defined
              }}
              style={{
                marginTop: '0.75rem',
                padding: '0.5rem 1rem',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollaboratorsModal; 